import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Popover,
  Row,
  Spin,
  Switch,
  Table,
  Typography,
} from 'antd';
import { passwordFormRules } from 'services';
import ClientObserversCard from './ClientObserversCard';
import {
  addAdminTrainee,
  AdminTrainerClassDetail,
  AdminTrainerClassTrainee,
  deleteAdminTrainee,
  deleteAdminTrainerClass,
  fetchAdminTrainerClassDetail,
  updateAdminTraineeDevplanAccess,
  updateAdminTraineeRecommendationAccess,
} from './api';

type Props = {
  klassId: number;
  onDeleted: (klassId: number) => void;
  visible: boolean;
  hide: () => void;
};

const requestErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const EditClassModal: React.FC<Props> = ({
  klassId,
  onDeleted,
  visible,
  hide,
}) => {
  const [form] = Form.useForm();
  const [klass, setKlass] = useState<AdminTrainerClassDetail>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [mutationError, setMutationError] = useState<string>();
  const [reloadVersion, setReloadVersion] = useState(0);
  const [busyTraineeId, setBusyTraineeId] = useState<number>();
  const [adding, setAdding] = useState(false);
  const [deletingClass, setDeletingClass] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(undefined);

    fetchAdminTrainerClassDetail(klassId)
      .then(result => {
        if (active) setKlass(result);
      })
      .catch(requestError => {
        if (active) {
          setLoadError(
            requestErrorMessage(
              requestError,
              'The class details could not be loaded.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [klassId, reloadVersion]);

  const updateDevplanAccess = async (
    trainee: AdminTrainerClassTrainee,
    checked: boolean,
  ) => {
    setBusyTraineeId(trainee.id);
    setMutationError(undefined);
    try {
      const result = await updateAdminTraineeDevplanAccess(
        klassId,
        trainee.id,
        checked,
      );
      setKlass(current =>
        current
          ? {
              ...current,
              trainees: current.trainees.map(item =>
                item.id === result.trainee_id
                  ? { ...item, can_see_devplan: result.can_see_devplan }
                  : item,
              ),
            }
          : current,
      );
    } catch (requestError) {
      setMutationError(
        requestErrorMessage(
          requestError,
          'Development Plan access could not be updated.',
        ),
      );
    } finally {
      setBusyTraineeId(undefined);
    }
  };

  const updateRecommendationAccess = async (
    trainee: AdminTrainerClassTrainee,
    checked: boolean,
  ) => {
    setBusyTraineeId(trainee.id);
    setMutationError(undefined);
    try {
      const result = await updateAdminTraineeRecommendationAccess(
        klassId,
        trainee.id,
        checked,
      );
      setKlass(current =>
        current
          ? {
              ...current,
              trainees: current.trainees.map(item =>
                item.id === result.trainee_id
                  ? {
                      ...item,
                      can_see_recommendations: result.can_see_recommendations,
                    }
                  : item,
              ),
            }
          : current,
      );
    } catch (requestError) {
      setMutationError(
        requestErrorMessage(
          requestError,
          'Recommendation access could not be updated.',
        ),
      );
    } finally {
      setBusyTraineeId(undefined);
    }
  };

  const deleteTrainee = async (trainee: AdminTrainerClassTrainee) => {
    setBusyTraineeId(trainee.id);
    setMutationError(undefined);
    try {
      const result = await deleteAdminTrainee(trainee.id);
      setKlass(current =>
        current
          ? {
              ...current,
              trainees: current.trainees.filter(
                item => item.id !== result.trainee_id,
              ),
            }
          : current,
      );
    } catch (requestError) {
      setMutationError(
        requestErrorMessage(requestError, 'The trainee could not be deleted.'),
      );
    } finally {
      setBusyTraineeId(undefined);
    }
  };

  const columns = [
    {
      title: 'Trainees',
      key: 'username',
      dataIndex: 'username',
    },
    {
      title: 'Development Plan',
      key: 'can_see_devplan',
      width: 150,
      align: 'center' as const,
      render: (_value: unknown, trainee: AdminTrainerClassTrainee) => (
        <Switch
          aria-label={`Development Plan access for ${trainee.username}`}
          checked={trainee.can_see_devplan}
          loading={busyTraineeId === trainee.id}
          onChange={checked => updateDevplanAccess(trainee, checked)}
        />
      ),
    },
    {
      title: 'Recommendations',
      key: 'can_see_recommendations',
      width: 150,
      align: 'center' as const,
      render: (_value: unknown, trainee: AdminTrainerClassTrainee) => (
        <Switch
          aria-label={`Recommendations access for ${trainee.username}`}
          checked={trainee.can_see_recommendations}
          loading={busyTraineeId === trainee.id}
          onChange={checked => updateRecommendationAccess(trainee, checked)}
        />
      ),
    },
    {
      title: 'Action',
      width: 80,
      align: 'center' as const,
      render: (_value: unknown, trainee: AdminTrainerClassTrainee) => (
        <Button
          type="link"
          loading={busyTraineeId === trainee.id}
          onClick={() => deleteTrainee(trainee)}
        >
          Delete
        </Button>
      ),
    },
  ];

  const body = () => {
    if (loading) {
      return <Spin aria-label="Loading class details" />;
    }
    if (loadError || !klass) {
      return (
        <Alert
          type="error"
          showIcon
          message={loadError || 'The class details could not be loaded.'}
          action={
            <Button size="small" onClick={() => setReloadVersion(v => v + 1)}>
              Retry
            </Button>
          }
        />
      );
    }

    return (
      <>
        {mutationError ? (
          <Alert
            type="error"
            showIcon
            message={mutationError}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Row gutter={50}>
          <Col span={12}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={klass.trainees}
              bordered
              locale={{
                emptyText: (
                  <Empty
                    description={
                      <Typography.Text type="secondary">
                        No trainees in this class.
                      </Typography.Text>
                    }
                  />
                ),
              }}
            />
            {klass.has_sessions ? (
              <Popover content="To delete a class, first delete all its sessions.">
                <Button danger disabled style={{ marginTop: 16 }}>
                  Delete class
                </Button>
              </Popover>
            ) : (
              <Popover content="This will also delete all trainee accounts associated with this class.">
                <Button
                  danger
                  loading={deletingClass}
                  onClick={async () => {
                    setDeletingClass(true);
                    setMutationError(undefined);
                    try {
                      const result = await deleteAdminTrainerClass(klass.id);
                      onDeleted(result.klass_id);
                      hide();
                    } catch (requestError) {
                      setMutationError(
                        requestErrorMessage(
                          requestError,
                          'The class could not be deleted.',
                        ),
                      );
                    } finally {
                      setDeletingClass(false);
                    }
                  }}
                  style={{ marginTop: 16 }}
                >
                  Delete class
                </Button>
              </Popover>
            )}
          </Col>
          <Col style={{ borderLeft: '2px solid #eeeeee' }} span={12}>
            <Card title="Add a new user to the class">
              <Form
                form={form}
                labelCol={{ span: 6 }}
                autoComplete="off"
                onFinish={async values => {
                  setAdding(true);
                  setMutationError(undefined);
                  try {
                    const result = await addAdminTrainee(
                      klassId,
                      values.username,
                      values.password,
                    );
                    setKlass(current =>
                      current
                        ? {
                            ...current,
                            trainees: [...current.trainees, result.trainee],
                          }
                        : current,
                    );
                  } catch (requestError) {
                    setMutationError(
                      requestErrorMessage(
                        requestError,
                        'The trainee could not be created.',
                      ),
                    );
                  } finally {
                    setAdding(false);
                  }
                }}
              >
                <Form.Item
                  label="Username"
                  name="username"
                  rules={[{ required: true }]}
                >
                  <Input autoComplete="off" />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={passwordFormRules}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Button htmlType="submit" loading={adding}>
                  Create user
                </Button>
              </Form>
            </Card>
            <ClientObserversCard klassId={klassId} />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Modal
      title={klass ? `Edit ${klass.name}` : 'Edit class'}
      open={visible}
      onCancel={hide}
      footer={null}
      width="800px"
    >
      {body()}
    </Modal>
  );
};

export default EditClassModal;
